import { queryOptions } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { brandConfig, brandDbNameMap } from "@/lib/brandConfig";
import { productHubSlugs } from "@/lib/productHubConfig";

/**
 * Brands whose hub lists articles by tag (case-insensitive) instead of a
 * title/content text search, and also includes generic `products` rows.
 */
export const brandArticleTags: Record<string, string[]> = {
  "jcb-tools": ["JCB Tools"],
};

/** Extra brand spellings used in the generic products table. */
const brandGenericProductNames: Record<string, string[]> = {
  "jcb-tools": ["JCB", "JCB Tools", "JCB 18V Tools"],
};

const PRODUCT_SELECT =
  "id, brand, model, slug, image_url, rating, price_gbp, previous_price_gbp, price_drop_at, sale_end_date, description, best_for, affiliate_link";

const ARTICLE_SELECT =
  "id, title, slug, excerpt, featured_image, featured_image_alt, category, categories, tags, published_at";

const tagVariants = (tag: string) =>
  Array.from(new Set([tag, tag.toLowerCase(), tag.toUpperCase(), tag.replace(/\b\w/g, (c) => c.toUpperCase()),
    tag.charAt(0).toUpperCase() + tag.slice(1).toLowerCase(), tag.split(" ").map((w, i) => (i === 0 ? w : w.toLowerCase())).join(" ")]));

export const brandArticlesQueryOptions = (brandSlug: string) =>
  queryOptions({
    queryKey: ["brand-articles", brandSlug],
    queryFn: async () => {
      const tags = brandArticleTags[brandSlug];
      if (tags) {
        const variants = tags.flatMap(tagVariants);
        const wanted = new Set(tags.map((t) => t.toLowerCase()));
        const { data, error } = await supabase
          .from("articles")
          .select(ARTICLE_SELECT)
          .eq("status", "published")
          .overlaps("tags", variants)
          .order("published_at", { ascending: false })
          .limit(500);
        if (error) throw error;
        return (data || []).filter((a) => (a.tags || []).some((t) => wanted.has(t.toLowerCase())));
      }
      const config = brandConfig[brandSlug];
      const searchName = brandDbNameMap[brandSlug]?.[0] || config?.name || "";
      const { data } = await supabase
        .from("articles")
        .select(ARTICLE_SELECT)
        .eq("status", "published")
        .or(`title.ilike.%${searchName}%,content.ilike.%${searchName}%`)
        .order("published_at", { ascending: false })
        .limit(100);
      return data || [];
    },
  });

type ProductTable = "lawn_mowers" | "chainsaws" | "generators" | "pressure_washers";

export const brandProductsQueryOptions = (brandSlug: string, table: ProductTable) =>
  queryOptions({
    queryKey: ["brand-products", brandSlug, table],
    queryFn: async () => {
      const dbNames = brandDbNameMap[brandSlug] || [];
      if (!dbNames.length) return [];
      const { data } = await supabase
        .from(table)
        .select(PRODUCT_SELECT)
        .eq("status", "published")
        .in("brand", dbNames)
        .order("rating", { ascending: false });
      return data || [];
    },
  });

export const brandGenericProductsQueryOptions = (brandSlug: string) =>
  queryOptions({
    queryKey: ["brand-generic-products", brandSlug],
    queryFn: async () => {
      const names = brandGenericProductNames[brandSlug];
      if (!names) return [];
      const { data, error } = await supabase
        .from("products")
        .select(`${PRODUCT_SELECT}, product_type`)
        .eq("status", "published")
        .in("brand", names)
        .order("rating", { ascending: false });
      if (error) throw error;
      return (data || []).filter((p) => productHubSlugs.includes(p.product_type));
    },
  });

export const BRAND_PRODUCT_TABLES: ProductTable[] = ["lawn_mowers", "chainsaws", "generators", "pressure_washers"];
